<template>
  <with-loader id="tool-usages" class="p-14 w-full h-full" :loading="isLoading">
    <div class="text-gray-dark text-lg pb-5 border-solid border-b border-0 border-gray w-full" v-html="summaryText"/>

    <perfect-scrollbar class="pr-4 pb-4 tool-usages-list">
      <div v-for="itemType in ['answers', 'events', 'blocks']" :key="itemType">
        <div v-if="!isEmpty(getRefItems(itemType))" class="py-3 mt-6 flex gap-1 items-baseline">
          <span class="text-lg font-bold ml-px capitalize text-gray-dark">{{ itemType }}</span>
          <component :is="iconMap[itemType]" class="h-4"/>
        </div>

        <div class="flex flex-col gap-2">
          <div v-for="ref in getRefItems(itemType)" :key="ref.id" class="flex items-center justify-between gap-1">
            <span class="text-base text-gray-dark truncate" :class="{ 'w-1/2': hasMultipleBots }">{{ ref.name }}</span>

            <div class="flex items-center" :class="hasMultipleBots ? 'w-1/2 justify-between gap-1' : 'justify-end'">
              <span v-if="hasMultipleBots" class="text-base text-left text-gray truncate" :title="getBotName(ref.bot_id)">
                {{ getBotName(ref.bot_id) }}
              </span>
              <base-button variant="bordered"
                           class="h-8 px-4 items-center"
                           :router-link="itemRoute(ref, itemType)"
                           @click.native="$emit('show-item')"
              >
                Show
              </base-button>
            </div>
          </div>
        </div>
      </div>
    </perfect-scrollbar>
  </with-loader>
</template>

<script>
import AnswersIcon from '@/assets/images/icons/AnswersIcon'
import BaseButton from '@/components/base-components/BaseButton.vue'
import BlocksIcon from '@/assets/images/icons/BlocksIcon'
import EventsIcon from '@/assets/images/icons/EventsIcon'
import WithLoader from '@/components/common/WithLoader.vue'
import botModule from '@/store/modules/bot.module'
import isEmpty from 'ramda/src/isEmpty'
import toolsModule from '@/store/modules/tools.module'
import { PerfectScrollbar } from 'vue2-perfect-scrollbar'

export default {
  name: 'ToolUsages',

  components: { AnswersIcon, BaseButton, BlocksIcon, EventsIcon, PerfectScrollbar, WithLoader },

  props: {
    toolId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      isLoading: false,
      hasError: false,
      answers: [],
      events: [],
      blocks: []
    }
  },

  computed: {
    botList: botModule.getBotList,
    tools: toolsModule.tools,

    hasMultipleBots() {
      return this.botList.length > 1
    },

    toolName() {
      return (this.tools.find(tool => tool.id === this.toolId) || {}).name || 'This tool'
    },

    iconMap() {
      return { answers: AnswersIcon, events: EventsIcon, blocks: BlocksIcon }
    },

    summaryText() {
      // The toast already carries the reason; this panel just must not claim the tool is unused when
      // the lookup that would prove that never came back.
      if (this.hasError) return `Could not check where <strong>${this.toolName}</strong> is used.`

      const parts = []
      if (this.answers.length) parts.push(`${this.answers.length} answer${this.answers.length !== 1 ? 's' : ''}`)
      if (this.events.length) parts.push(`${this.events.length} event${this.events.length !== 1 ? 's' : ''}`)
      if (this.blocks.length) parts.push(`${this.blocks.length} block${this.blocks.length !== 1 ? 's' : ''}`)

      // Empty state doubles as the explanation of why delete is available — it is the same panel the
      // failed delete opens, so it has to answer "why can't I remove this?" in both directions.
      if (!parts.length) return `<strong>${this.toolName}</strong> is not used in any answer, event or block, so it can be deleted.`

      const list = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0]
      return `<strong>${this.toolName}</strong> is used in ${list}. Remove it there before deleting it.`
    }
  },

  methods: {
    isEmpty,

    getRefItems(itemType) {
      return this[itemType]
    },

    getBotName(botId) {
      return (this.botList.find(bot => bot.id === botId) || {}).name || botId
    },

    itemRoute(ref, itemType) {
      return `/bots/${ref.bot_id}/${itemType}/${ref.id}`
    },

    async fetchRefs() {
      this.isLoading = true
      try {
        const { templates, answers, events } = await toolsModule.fetchReferences(this.toolId)
        this.blocks = templates
        this.answers = answers
        this.events = events
      } catch (err) {
        this.hasError = true
      } finally {
        this.isLoading = false
      }
    }
  },

  mounted() {
    this.fetchRefs()
  }
}
</script>

<style lang="scss" scoped>
.tool-usages-list {
  height: calc(100% - 100px);
}
</style>
